import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AgentChatProvider, type ToolAction } from "@/components/AgentChatProvider";
import { useBrand } from "@/branding/BrandProvider";
import { useAgentArea } from "@/hooks/useAgentArea";

/** Sandbox specifics over the router-agnostic <AgentChatProvider>; unrecognised tool actions are ignored. */
export function ChatProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const brand = useBrand();
  const { pathname, search } = useLocation();
  // Initial-only: open on the home route for users with no saved preference.
  const defaultOpen = pathname === "/";
  const { area, label, pageLabel } = useAgentArea();
  const books = area === "books";
  const title = pageLabel ?? label;
  // The API caps the path at 500; a long filter query must not fail the message.
  const page = useMemo(
    () => (title ? { path: (pathname + search).slice(0, 500), title } : undefined),
    [pathname, search, title],
  );

  const handleAction = useCallback(
    (action: ToolAction) => {
      const id = typeof action.params?.id === "string" ? action.params.id : null;
      switch (action.type) {
        case "open_invoice":
          if (id) navigate(`/invoices/${id}`);
          break;
        case "open_customer":
          if (id) navigate(`/customers/${id}`);
          break;
        case "open_supplier":
          if (id) navigate(`/suppliers/${id}`);
          break;
        case "open_bill":
          if (id) navigate(`/bills/${id}`);
          break;
        case "open_expense":
          if (id) navigate(`/expenses/${id}`);
          break;
        case "open_merchant":
          if (id) navigate(`/expenses/merchants/${id}`);
          break;
        case "open_product":
          if (id) navigate(`/products/${id}`);
          break;
        case "open_invoices":
          navigate("/invoices");
          break;
        case "open_customers":
          navigate("/customers");
          break;
        case "open_suppliers":
          navigate("/suppliers");
          break;
        case "open_bills":
          navigate("/bills");
          break;
        case "open_products":
          navigate("/products");
          break;
        // No dedicated payments list yet — invoices is the closest surface for collected cash.
        case "open_payments":
          navigate("/invoices");
          break;
        case "open_organisation":
          navigate("/organisation/settings");
          break;
        case "open_payment_account":
          navigate("/settings/payments");
          break;
        case "open_onboarding":
          navigate(action.params?.target === "ar" ? "/ar" : "/ap");
          break;
      }
    },
    [navigate],
  );

  return (
    <AgentChatProvider
      agent={books ? "books" : "all"}
      agentName={books ? `${brand.name} Books` : brand.name}
      page={page}
      defaultOpen={defaultOpen}
      onAction={handleAction}
    >
      {children}
    </AgentChatProvider>
  );
}

/** Existing consumers import the chat hook from here; point it at AgentChatProvider. */
export { useAgentChat as useChat } from "@/components/AgentChatProvider";
